import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import {
  Calendar,
  Plus,
  X,
  Search,
  Filter,
  User,
  Star,
  Users,
  Edit,
  Trash2,
  Save,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Copy,
  Check,
  ExternalLink,
  Settings,
} from 'lucide-react';
import {
  candidateApi,
  interviewApi,
  companyApi,
  jobTypeApi,
  jobPostingApi,
  JobPosting,
  JobType,
} from '../../services/api';
import { safeToInterviewTypeLabel } from '../../types/backendEnums';
import RecruiterInterviewModal from './modals/InterviewModal';
import { InterviewSession } from '../../types/models';
import {
  RecruiterCandidate,
  RecruiterEvaluation,
  CompleteInterviewManagementProps,
} from '../../types/recruiter';
import { EvaluationSidebar } from './components/EvaluationSidebar';
import { InterviewsTab } from './tabs/InterviewsTab';
import { environmentConfig } from '../../config/environment';
import AdminHeader from '../common/AdminHeader';
import { Pagination } from '../common/Pagination';
import { usePagination } from '../../hooks/usePagination';

// キャッシュ用の型定義
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// Props type now imported from types/recruiter.ts

// キャッシュマネージャー
class CacheManager {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5分

  set<T>(key: string, data: T, ttl = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }

    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
}

// グローバルキャッシュインスタンス
const cache = new CacheManager();

// Using RecruiterCandidate type from types/recruiter.ts instead of local interface

// 面接ステータスの定義（一箇所で管理）
const INTERVIEW_STATUS_CONFIG = {
  not_started: { label: '未開始', color: 'bg-gray-100 text-gray-800' },
  in_progress: { label: '進行中', color: 'bg-orange-100 text-orange-800' },
  completed: { label: '完了', color: 'bg-green-100 text-green-800' },
  evaluated: { label: '評価済み', color: 'bg-blue-100 text-blue-800' },
  cancelled: { label: 'キャンセル', color: 'bg-red-100 text-red-800' },
} as const;
const INTERVIEW_STATUSES = Object.keys(INTERVIEW_STATUS_CONFIG) as (keyof typeof INTERVIEW_STATUS_CONFIG)[];
type InterviewStatus = keyof typeof INTERVIEW_STATUS_CONFIG;

interface Interview {
  id: number;
  candidateId?: number | string;
  candidateName: string;
  position: string;
  startDate: string;
  endDate: string;
  type: string;
  status: InterviewStatus;
  interviewer: string;
  score?: number;
  interviewUrl?: string;
  sessionId?: string;
  googleDriveFolderUrl?: string;
  googleDriveFolderPath?: string;
  jobPostingId?: string | null;
  completedAt?: string | null;
}

interface SortConfig {
  key: keyof RecruiterCandidate | null;
  direction: 'asc' | 'desc';
}

interface InterviewSortConfig {
  key: keyof Interview | null;
  direction: 'asc' | 'desc';
}

type InterviewStatusFilter = 'all' | InterviewStatus;

interface EditCandidateModalProps {
  candidate: RecruiterCandidate;
}

const CompleteInterviewManagement: React.FC<CompleteInterviewManagementProps> = ({
  companyId,
  companyName,
}) => {
  const [currentTab, setCurrentTab] = useState('candidates');
  const [selectedCandidate, setSelectedCandidate] = useState<RecruiterCandidate | null>(null);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [showEditCandidateModal, setShowEditCandidateModal] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showEvaluationSidebar, setShowEvaluationSidebar] = useState(false);
  const [showInterviewDetailModal, setShowInterviewDetailModal] = useState(false);
  const [selectedInterviewIndex, setSelectedInterviewIndex] = useState<number>(0);
  const [currentDate, setCurrentDate] = useState(new Date()); // 現在の日付
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });
  const [interviewSortConfig, setInterviewSortConfig] = useState<InterviewSortConfig>({
    key: 'startDate',
    direction: 'desc',
  });
  const [interviewStatusFilter, setInterviewStatusFilter] = useState<InterviewStatusFilter>('all');
  const [interviewSearchTerm, setInterviewSearchTerm] = useState('');
  const [showInterviewDeleteConfirmDialog, setShowInterviewDeleteConfirmDialog] = useState(false);
  const [interviewToDelete, setInterviewToDelete] = useState<Interview | null>(null);

  const [interviewForm, setInterviewForm] = useState({
    candidateId: '',
    startDate: '',
    endDate: '',
    type: '一次面接',
    interviewer: '',
    jobPostingId: '',
  });

  // URLコピー状態管理
  const [copiedUrls, setCopiedUrls] = useState<{ [key: number]: boolean }>({});

  // APIから取得するデータ
  const [candidates, setCandidates] = useState<RecruiterCandidate[]>([]);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [jobPostingLoading, setJobPostingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'network' | 'auth' | 'validation' | 'server' | null>(
    null
  );
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRY_COUNT = 3;

  // 楽観的更新のためのローカル状態管理
  const [optimisticUpdates, setOptimisticUpdates] = useState<{
    candidates: RecruiterCandidate[];
    interviews: Interview[];
  }>({ candidates: [], interviews: [] });
  const [isOptimisticMode, setIsOptimisticMode] = useState(false);

  // ユーザーメニューの状態管理

  // 安全な数値変換関数
  const safeParseInt = (value: any, fallback: number = 0): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value);
      return isNaN(parsed) ? fallback : parsed;
    }
    return fallback;
  };



  const normalizeEvaluation = useCallback(
    (apiEvaluation: any): RecruiterEvaluation | undefined => {
      if (!apiEvaluation) return undefined;
      const detailed = apiEvaluation.detailedEvaluation || {};
      const specializedSkills = detailed.specializedSkills;
      const genericSkills = detailed.genericSkills;
      const customSkills = detailed.customSkills;

      return {
        totalScore: apiEvaluation.overallScore ?? 0,
        rank: apiEvaluation.rank,
        recommendation: apiEvaluation.recommendation,
        summary: apiEvaluation.summary,
        evaluationText: apiEvaluation.evaluationText,
        careerSummary: apiEvaluation.careerSummary,
        evaluatedAt: apiEvaluation.evaluatedAt,
        detailedEvaluation: {
          specializedSkills,
          genericSkills,
          customSkills,
          fixedQuestionAnswers: detailed.fixedQuestionAnswers,
        },
        storage: apiEvaluation.storage,
        version: apiEvaluation.version,
      };
    },
    []
  );

  // データ変換ユーティリティ関数
  const convertAPICandidateToLocal = useCallback((apiCandidate: any): RecruiterCandidate => {
    const normalizedAssignees = (apiCandidate.assignees || [])
      .map((a: any) => ({
        id: String(a.id || a.companyUserId || a.companyUser?.id || ''),
        email: a.email || a.companyUser?.email,
        displayName: a.displayName || a.companyUser?.displayName,
      }))
      .filter((a: any) => a.id);
    return {
      id: apiCandidate.id, // IDをそのまま文字列として使用
      name: apiCandidate.name,
      email: apiCandidate.email,
      phone: apiCandidate.phone || '',
      position: apiCandidate.position || '',
      candidateType: apiCandidate.candidateType || 'chuuto',
      status: apiCandidate.status || 'pending', // APIから取得したステータスを使用
      inviteDate:
        apiCandidate.inviteDate ||
        (apiCandidate.created_at
          ? new Date(apiCandidate.created_at).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0]),
      careerSummary: apiCandidate.careerSummary, // 経歴サマリーを追加
      assignees: normalizedAssignees,
      assigneeUserIds: normalizedAssignees.map((a: any) => a.id).filter(Boolean),
      interviews: (apiCandidate.interviews || []).map((interview: any) => {
        return {
          ...interview,
          id: interview.id,
          sessionId: interview.id,
          endDate: interview.endDate || interview.date || '',
          completedAt: interview.completedAt || null,
          evaluation: normalizeEvaluation(interview.evaluation),
        };
      }),
      evaluation: normalizeEvaluation(apiCandidate.evaluation), // 評価データを追加
    };
  }, [normalizeEvaluation]);

  const convertInterviewSessionToLocal = useCallback((session: InterviewSession): Interview => {
    // IDが数値に変換できない場合は、タイムスタンプベースのIDを生成
    const interviewId = safeParseInt(session.id, Date.now() + Math.random());

    return {
      id: interviewId,
      candidateName: session.candidateName || '未設定',
      position: session.position || '未設定',
      startDate: session.date || '',
      endDate: session.endDate || session.date || '', // endDateがある場合は使用、なければdateを使用
      type: session.type || '面接',
      status: (INTERVIEW_STATUSES.includes(session.status as InterviewStatus)
        ? session.status
        : 'not_started') as InterviewStatus,
      interviewer: '面接官',
      interviewUrl: session.interviewUrl || '',
      sessionId: session.id,
      googleDriveFolderUrl: session.resultUrl || undefined,
      googleDriveFolderPath: session.googleDriveFolderPath || undefined,
      jobPostingId: (session as any).jobPostingId || null,
      completedAt: (session as any).completedAt || null,
    };
  }, []);

  // エラー処理ユーティリティ
  const handleError = useCallback((err: any, context: string) => {
    console.error(`❌ Error in ${context}:`, err);
    setRetryCount((prev) => prev + 1);

    if (err?.code === 'NETWORK_ERROR' || err?.message?.includes('Network Error')) {
      setErrorType('network');
      setError('ネットワークエラー: インターネット接続を確認してください');
    } else if (err?.response?.status === 401) {
      setErrorType('auth');
      setError('認証エラー: ログインし直してください');
    } else if (err?.response?.status === 400) {
      setErrorType('validation');
      setError('入力エラー: ' + (err?.response?.data?.message || '入力内容を確認してください'));
    } else if (err?.response?.status >= 500) {
      setErrorType('server');
      setError('サーバーエラー: しばらく時間をおいてから再度お試しください');
    } else {
      setErrorType('server');
      setError(err instanceof Error ? err.message : '不明なエラーが発生しました');
    }
  }, []);

  // リトライ機能付きのデータ取得
  const fetchCandidatesWithRetry = useCallback(
    async (useCache = true, attempt = 0) => {
      try {
        setError(null);
        setErrorType(null);

        // キャッシュキーに企業IDを含める
        const cacheKey = `candidates_${companyId}`;

        // キャッシュチェック
        if (useCache && attempt === 0) {
          const cachedData = cache.get<RecruiterCandidate[]>(cacheKey);
          if (cachedData) {
            console.log('📦 Using cached candidates data');
            setCandidates(cachedData);
            return;
          }
        }

        const response = await candidateApi.getCandidates(companyId);
        if (response.success && response.data) {
          const localCandidates = response.data.map(convertAPICandidateToLocal);
          setCandidates(localCandidates);
          cache.set(cacheKey, localCandidates);
          setRetryCount(0); // リセット
        } else {
          throw new Error(response.message || 'データの取得に失敗しました');
        }
      } catch (err) {
        if (attempt < MAX_RETRY_COUNT - 1) {
          console.log(`🔄 Retrying... (${attempt + 1}/${MAX_RETRY_COUNT})`);
          setTimeout(() => fetchCandidatesWithRetry(false, attempt + 1), 1000 * (attempt + 1));
        } else {
          handleError(err, 'fetchCandidates');
        }
      }
    },
    [handleError, companyId, convertAPICandidateToLocal]
  );

  const fetchInterviewsWithRetry = useCallback(
    async (useCache = true, attempt = 0) => {
      try {
        setError(null);
        setErrorType(null);

        // キャッシュキーに企業IDを含める
        const cacheKey = `interviews_${companyId}`;

        // キャッシュチェック
        if (useCache && attempt === 0) {
          const cachedData = cache.get<Interview[]>(cacheKey);
          if (cachedData) {
            console.log('📦 Using cached interviews data');
            setInterviews(cachedData);
            return;
          }
        }

        console.log(
          `🌐 Fetching fresh interviews data (attempt ${attempt + 1}) for company: ${companyId}`
        );
        const response = await interviewApi.getInterviews(companyId);
        if (response.success && response.data) {
          const localInterviews = response.data.map(convertInterviewSessionToLocal);
          setInterviews(localInterviews);
          cache.set(cacheKey, localInterviews);
          setRetryCount(0); // リセット
        } else {
          throw new Error(response.message || 'データの取得に失敗しました');
        }
      } catch (err) {
        if (attempt < MAX_RETRY_COUNT - 1) {
          console.log(`🔄 Retrying... (${attempt + 1}/${MAX_RETRY_COUNT})`);
          setTimeout(() => fetchInterviewsWithRetry(false, attempt + 1), 1000 * (attempt + 1));
        } else {
          handleError(err, 'fetchInterviews');
        }
      }
    },
    [handleError, companyId, convertInterviewSessionToLocal]
  );

  const fetchJobPostings = useCallback(async () => {
    try {
      setJobPostingLoading(true);
      const response = await jobPostingApi.getJobPostings(companyId);
      if (response.success && response.data) {
        setJobPostings(response.data);
      }
    } catch (err) {
      console.error('❌ Error in fetchJobPostings:', err);
    } finally {
      setJobPostingLoading(false);
    }
  }, [companyId]);

  // 候補者詳細を面接情報付きで取得する関数
  const fetchCandidateWithInterviews = useCallback(
    async (candidateId: string) => {
      try {
        setError(null);
        setErrorType(null);

        // キャッシュを一時的に無効化してテスト
        cache.invalidate('candidates');
        const response = await candidateApi.getCandidateWithInterviews(candidateId);
        if (response.success && response.data) {
          // APIデータをフロントエンド用のRecruiterCandidate型に変換
          const normalizedAssignees = (response.data.assignees || []).map((a: any) => ({
            id: String(a.id || a.companyUserId || a.companyUser?.id || ''),
            email: a.email || a.companyUser?.email,
            displayName: a.displayName || a.companyUser?.displayName,
          }));
          const candidateWithInterviews: RecruiterCandidate = {
            id: response.data.id, // IDを文字列として保持
            name: response.data.name,
            email: response.data.email,
            phone: response.data.phone || '',
            position:
              (response.data as any).jobType ||
              (response.data as any).job_type ||
              response.data.position ||
              '',
            status: response.data.status as 'invited' | 'pending' | 'completed',
            inviteDate: response.data.inviteDate || new Date().toISOString().split('T')[0],
            careerSummary: response.data.careerSummary, // 経歴サマリーを追加
            assignees: normalizedAssignees,
            assigneeUserIds: normalizedAssignees.map((a: any) => a.id).filter(Boolean),
            // 以下のフィールドはDBスキーマに存在しないため、デフォルト値または未設定とする
            resumeUrl:
              (response.data as any).resumeUrl || (response.data as any).resume_url || undefined,
            experience: (response.data as any).experience || undefined,
            skills: (response.data as any).skills || undefined,
            interviews:
              response.data.interviews?.map((interview) => {
                // startTime と endTime から timeRange を生成
                const getTimeRange = (startTime?: string, endTime?: string) => {
                  if (!startTime) return '';
                  const start = startTime.includes('T')
                    ? startTime.split('T')[1]?.substring(0, 5)
                    : startTime;
                  if (!endTime) return start || '';
                  const end = endTime.includes('T')
                    ? endTime.split('T')[1]?.substring(0, 5)
                    : endTime;
                  return `${start} - ${end}`;
                };

                return {
                  id: interview.id, // 面接IDを追加
                  sessionId: interview.id, // sessionIdも追加
                  date: interview.date || '',
                  endDate: (interview as any).endDate || interview.date || '',
                  timeRange: getTimeRange(interview.startTime, interview.endTime),
                  status:
                    (interview.status as
                      | 'scheduled'
                      | 'completed'
                      | 'in_progress'
                      | 'not_started'
                      | 'cancelled') || 'scheduled',
                  type: interview.type || '面接',
                  evaluation: normalizeEvaluation(interview.evaluation),
                  score: normalizeEvaluation(interview.evaluation)?.totalScore,
                  googleDriveFolderUrl:
                    (interview as any).googleDriveFolderPath ||
                    (interview as any).resultUrl ||
                    undefined,
                  googleDriveFolderPath: (interview as any).googleDriveFolderPath || undefined,
                  completedAt: (interview as any).completedAt || null,
                };
              }) || [],
            evaluation: normalizeEvaluation(response.data.evaluation),
          };
          setSelectedCandidate(candidateWithInterviews);
          setSelectedInterviewIndex(0); // 最初の面接を選択
          setShowEvaluationSidebar(true);
        } else {
          throw new Error(response.message || '候補者詳細の取得に失敗しました');
        }
      } catch (err) {
        console.error('🚨 候補者詳細取得エラー:', err);
        console.error('🚨 Error stack:', err instanceof Error ? err.stack : 'No stack trace');
        console.error('🚨 Error details:', {
          message: err instanceof Error ? err.message : String(err),
          candidateId,
          timestamp: new Date().toISOString(),
        });
        handleError(err, 'fetchCandidateWithInterviews');
      }
    },
    [handleError, normalizeEvaluation]
  );

  // 楽観的更新ヘルパー関数
  const performOptimisticUpdate = useCallback(
    (
      type: 'candidate' | 'interview',
      action: 'add' | 'update' | 'delete',
      data?: any,
      id?: string | number
    ) => {
      setIsOptimisticMode(true);

      if (type === 'candidate') {
        if (action === 'add' && data) {
          const newCandidate: RecruiterCandidate = {
            id: `temp-candidate-${Date.now()}`, // 一時的な文字列ID
            ...data,
            status: 'invited', // 新規候補者は招待済み状態
            inviteDate: new Date().toISOString().split('T')[0],
            // オプショナルフィールドは未設定のままにする
            resumeUrl: undefined,
            experience: undefined,
            skills: undefined,
            interviews: [],
          };
          setOptimisticUpdates((prev) => ({
            ...prev,
            candidates: [...prev.candidates, newCandidate],
          }));
        }
      } else if (type === 'interview') {
        if (action === 'add' && data) {
          const newInterview: Interview = {
            id: Date.now(), // 一時的なID
            candidateId: data.candidateId,
            candidateName: data.candidateName,
            startDate: data.date,
            endDate: data.date,
            type: data.type,
            status: 'not_started',
            interviewer: '面接官',
            position: data.position || '未設定',
          };
          setOptimisticUpdates((prev) => ({
            ...prev,
            interviews: [...prev.interviews, newInterview],
          }));
        }
      }
    },
    []
  );

  // 楽観的更新のロールバック
  const rollbackOptimisticUpdate = useCallback(() => {
    setIsOptimisticMode(false);
    setOptimisticUpdates({ candidates: [], interviews: [] });
  }, []);

  // 楽観的更新の確定
  const confirmOptimisticUpdate = useCallback(() => {
    setIsOptimisticMode(false);
    setOptimisticUpdates({ candidates: [], interviews: [] });
  }, []);

  // 表示用データ（楽観的更新を考慮）
  const displayCandidates = useMemo(() => {
    if (isOptimisticMode && optimisticUpdates.candidates.length > 0) {
      return [...candidates, ...optimisticUpdates.candidates];
    }
    return candidates;
  }, [candidates, optimisticUpdates.candidates, isOptimisticMode]);

  const displayInterviews = useMemo(() => {
    if (isOptimisticMode && optimisticUpdates.interviews.length > 0) {
      return [...interviews, ...optimisticUpdates.interviews];
    }
    return interviews;
  }, [interviews, optimisticUpdates.interviews, isOptimisticMode]);

  const getStatusColor = (status: string) => {
    const colors = {
      invited: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      scheduled: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-red-100 text-red-800',
      in_progress: 'bg-orange-100 text-orange-800',
      not_started: 'bg-gray-100 text-gray-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  // 採用区分（新卒/中途）の表示。候補者登録モーダルの選択肢と表記を揃える。
  // 未設定の既存データはDB既定値と同じ「中途」として扱う。
  const getCandidateTypeLabel = (candidateType?: 'chuuto' | 'shinsotsu') =>
    candidateType === 'shinsotsu' ? '新卒' : '中途';

  const getCandidateTypeColor = (candidateType?: 'chuuto' | 'shinsotsu') =>
    candidateType === 'shinsotsu'
      ? 'bg-purple-100 text-purple-800'
      : 'bg-sky-100 text-sky-800';

  const getStatusText = (status: string) => {
    const texts = {
      invited: '招待済み',
      pending: '面接予定',
      completed: '面接完了',
      scheduled: '予定',
      cancelled: 'キャンセル',
      in_progress: '進行中',
      not_started: '未開始',
    };
    return texts[status as keyof typeof texts] || status;
  };

  // 面接管理画面用のステータス表示（INTERVIEW_STATUS_CONFIGを参照）
  const getInterviewStatusColor = (status: string) => {
    const config = INTERVIEW_STATUS_CONFIG[status as InterviewStatus];
    return config?.color || 'bg-gray-100 text-gray-800';
  };

  const getInterviewStatusText = (status: string) => {
    const config = INTERVIEW_STATUS_CONFIG[status as InterviewStatus];
    return config?.label || status;
  };

  const handleScheduleInterview = (candidate: RecruiterCandidate) => {
    setInterviewForm({
      candidateId: candidate.id,
      startDate: '',
      endDate: '',
      type: '一次面接',
      interviewer: '',
      jobPostingId: '',
    });
    setShowInterviewModal(true);
  };

  // 候補者情報編集のハンドラー
  const handleEditCandidate = () => {
    setShowEditCandidateModal(true);
  };

  // 候補者情報更新のハンドラー
  const handleUpdateCandidate = async (updatedData: {
    name: string;
    email: string;
    phone: string;
    position: string;
    candidateType?: 'chuuto' | 'shinsotsu';
    jobTypeId?: string;
    jobTypeLabel?: string;
    assigneeUserIds?: string[];
  }) => {
    if (!selectedCandidate) return;

    try {
      setError(null);
      setErrorType(null);

      console.log('🔄 Updating candidate:', selectedCandidate.id);
      const response = await candidateApi.updateCandidate(selectedCandidate.id, updatedData);

      if (response.success && response.data) {
        // ローカル状態を更新（担当者も反映）
        const updatedCandidate = {
          ...selectedCandidate,
          ...updatedData,
          assigneeUserIds: updatedData.assigneeUserIds ?? selectedCandidate.assigneeUserIds,
        };
        setSelectedCandidate(updatedCandidate);

        // 候補者リストを更新
        setCandidates((prev) =>
          prev.map((c) => (c.id === selectedCandidate.id ? updatedCandidate : c))
        );

        // キャッシュを無効化
        cache.invalidate();

        // 最新情報で再取得して詳細表示を即時更新（担当者名含む）
        await fetchCandidateWithInterviews(selectedCandidate.id);

        setShowEditCandidateModal(false);
        console.log('✅ Candidate updated successfully');
      } else {
        throw new Error(response.message || '候補者情報の更新に失敗しました');
      }
    } catch (err) {
      console.error('❌ Update candidate error:', err);
      handleError(err, 'updateCandidate');
    }
  };

  // 候補者削除のハンドラー
  const handleDeleteCandidate = async () => {
    if (!selectedCandidate) return;

    try {
      setError(null);
      setErrorType(null);

      console.log('🗑️ Deleting candidate:', selectedCandidate.id);
      const response = await candidateApi.deleteCandidate(selectedCandidate.id);

      if (response.success) {
        // ローカル状態から削除
        setCandidates((prev) => prev.filter((c) => c.id !== selectedCandidate.id));

        // キャッシュを無効化
        cache.invalidate();

        // サイドバーを閉じる
        setShowEvaluationSidebar(false);
        setShowDeleteConfirmDialog(false);
        setSelectedCandidate(null);

        console.log('✅ Candidate deleted successfully');
      } else {
        throw new Error(response.message || '候補者の削除に失敗しました');
      }
    } catch (err) {
      console.error('❌ Delete candidate error:', err);
      handleError(err, 'deleteCandidate');
      setShowDeleteConfirmDialog(false);
    }
  };

  // 面接削除のハンドラー
  const handleDeleteInterview = useCallback(async () => {
    if (!interviewToDelete) return;

    try {
      setError(null);
      setErrorType(null);

      const targetId = String((interviewToDelete as any).sessionId || interviewToDelete.id);
      console.log('🗑️ Deleting interview:', targetId);
      const response = await interviewApi.deleteInterview(targetId);

      if (response.success) {
        // ローカル状態から削除
        setInterviews((prev) =>
          prev.filter(
            (iv) =>
              iv.id !== interviewToDelete.id &&
              iv.sessionId !== (interviewToDelete as any).sessionId
          )
        );

        // キャッシュを無効化
        cache.invalidate('interviews');

        // 必要なら最新データを再取得
        await fetchInterviewsWithRetry(false);

        // 関連モーダルを閉じる
        setShowInterviewDeleteConfirmDialog(false);
        setShowInterviewDetailModal(false);
        setSelectedInterview(null);
        setInterviewToDelete(null);

        console.log('✅ Interview deleted successfully');
      } else {
        throw new Error(response.message || '面接の削除に失敗しました');
      }
    } catch (err) {
      console.error('❌ Delete interview error:', err);
      handleError(err, 'deleteInterview');
      setShowInterviewDeleteConfirmDialog(false);
    }
  }, [interviewToDelete, fetchInterviewsWithRetry, handleError]);

  // フォームバリデーション（未使用のためコメントアウト）
  /* const validateInterviewForm = useCallback(() => {
    const errors: string[] = [];
    
    if (!interviewForm.candidateId) {
      errors.push('候補者を選択してください');
    }
    
    if (!interviewForm.startDate) {
      errors.push('面接日は必須です');
    } else {
      const selectedDate = new Date(interviewForm.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        errors.push('面接日は今日以降の日付を選択してください');
      }
    }
    
    if (!interviewForm.type) {
      errors.push('面接種別を選択してください');
    }
    
    return errors;
  }, [interviewForm]); */

  const handleCandidateFormSubmit = useCallback(
    async (formData: {
      name: string;
      email: string;
      phone: string;
      position: string;
      jobTypeId?: string;
      jobTypeLabel?: string;
      candidateType?: 'chuuto' | 'shinsotsu';
      assigneeUserIds?: string[];
    }) => {
      // バリデーション - フォームデータを直接使用
      const errors: string[] = [];

      if (!formData.name.trim()) {
        errors.push('候補者名を入力してください');
      }

      if (!formData.email.trim()) {
        errors.push('メールアドレスを入力してください');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errors.push('有効なメールアドレスを入力してください');
      }

      if (!formData.position.trim()) {
        errors.push('職種を入力してください');
      }

      if (errors.length > 0) {
        setErrorType('validation');
        setError(errors.join(', '));
        return;
      }

      const position = formData.position.trim();

      const candidateData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        position,
        jobTypeId: formData.jobTypeId,
        jobTypeLabel: formData.jobTypeLabel || position,
        candidateType: formData.candidateType || 'chuuto',
        companyId: companyId, // プロパティから会社IDを追加
        assigneeUserIds: formData.assigneeUserIds || [],
      };

      // 楽観的更新を実行
      performOptimisticUpdate('candidate', 'add', candidateData);

      try {
        setError(null);
        setErrorType(null);

        const response = await candidateApi.createCandidate(candidateData);
        if (response.success) {
          console.log('候補者登録成功:', response.data);
          // 楽観的更新を確定
          confirmOptimisticUpdate();
          cache.invalidate('candidates');
          await fetchCandidatesWithRetry(false);
          setShowCandidateModal(false);
        } else {
          throw new Error(response.message || response.error || '候補者登録に失敗しました');
        }
      } catch (err) {
        // 楽観的更新をロールバック
        rollbackOptimisticUpdate();
        handleError(err, 'createCandidate');
      }
    },
    [
      performOptimisticUpdate,
      confirmOptimisticUpdate,
      rollbackOptimisticUpdate,
      fetchCandidatesWithRetry,
      handleError,
      companyId,
    ]
  );

  // 候補者名に「様」を追加するヘルパー関数
  const formatCandidateName = useCallback((name: string) => {
    return name ? `${name}様` : '未設定';
  }, []);

  // ソート機能（メモ化）
  const handleSort = useCallback(
    (key: keyof RecruiterCandidate) => {
      let direction: 'asc' | 'desc' = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const getSortedCandidates = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    const filteredCandidates = searchLower
      ? displayCandidates.filter((candidate) =>
          [candidate.name, candidate.email, candidate.position].some((field) =>
            field?.toLowerCase().includes(searchLower)
          )
        )
      : displayCandidates;
    const sortedCandidates = [...filteredCandidates];
    if (sortConfig.key) {
      sortedCandidates.sort((a, b) => {
        let aValue: any = a[sortConfig.key!];
        let bValue: any = b[sortConfig.key!];

        // 特別なソート処理
        if (sortConfig.key === 'evaluation') {
          aValue = a.evaluation ? a.evaluation.totalScore : 0;
          bValue = b.evaluation ? b.evaluation.totalScore : 0;
        } else if (sortConfig.key === 'inviteDate') {
          aValue = new Date(a.inviteDate);
          bValue = new Date(b.inviteDate);
        } else if (sortConfig.key === 'candidateType') {
          // 未設定は表示と同じく「中途」扱いにして、表示とソート順を一致させる
          aValue = a.candidateType || 'chuuto';
          bValue = b.candidateType || 'chuuto';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedCandidates;
  }, [displayCandidates, sortConfig, searchTerm]);

  // 候補者一覧のページング。検索・ソートは全件に対して効かせたうえで、
  // その結果をページごとに切り出す（API は全件返しているのでクライアント側で分割）。
  const candidateTableRef = useRef<HTMLDivElement>(null);
  const candidatePagination = usePagination(getSortedCandidates, {
    // 検索・ソートを変えたら1ページ目に戻す
    resetKey: `${searchTerm}|${sortConfig.key ?? ''}|${sortConfig.direction}`,
    // ページを変えたら一覧の先頭までスクロールする
    scrollTargetRef: candidateTableRef,
  });

  const getSortIcon = useCallback(
    (columnKey: keyof RecruiterCandidate) => {
      if (sortConfig.key !== columnKey) {
        return <ArrowUpDown size={14} className="text-gray-400" />;
      }
      return sortConfig.direction === 'asc' ? (
        <ArrowUp size={14} className="text-blue-600" />
      ) : (
        <ArrowDown size={14} className="text-blue-600" />
      );
    },
    [sortConfig]
  );

  // URL関連のユーティリティ関数
  const generateInterviewUrl = useCallback(
    (interviewId: number) => {
      // 環境に応じたベースURLを使用
      const baseUrl = environmentConfig.frontendBaseUrl;
      return `${baseUrl}/interview/${companyId}/${interviewId}`;
    },
    [companyId]
  );

  const copyToClipboard = useCallback(async (text: string, interviewId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrls((prev) => ({ ...prev, [interviewId]: true }));
      setTimeout(() => {
        setCopiedUrls((prev) => ({ ...prev, [interviewId]: false }));
      }, 2000);
    } catch (err) {
      console.error('URLのコピーに失敗しました:', err);
    }
  }, []);

  const updateInterviewUrl = useCallback(
    async (interviewId: number, newUrl: string) => {
      try {
        // ここでAPI呼び出しを行う（実装は後で追加）
        setInterviews((prev) =>
          prev.map((interview) =>
            interview.id === interviewId ? { ...interview, interviewUrl: newUrl } : interview
          )
        );
        console.log(`面接ID ${interviewId} のURLを更新: ${newUrl}`);
      } catch (err) {
        handleError(err, 'updateInterviewUrl');
      }
    },
    [handleError]
  );

  // 初回データロード
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // 楽観的更新をリセット
      setIsOptimisticMode(false);
      setOptimisticUpdates({ candidates: [], interviews: [] });

      // キャッシュを完全にクリア
      cache.invalidate();

      console.log('🔄 キャッシュクリア完了、最新データを取得中...');

      // 強制的にキャッシュを使わずに最新データを取得
      await Promise.all([
        fetchCandidatesWithRetry(false), // キャッシュを使わない
        fetchInterviewsWithRetry(false), // キャッシュを使わない
        fetchJobPostings(),
      ]);

      setLoading(false);
    };
    loadData();
  }, [fetchCandidatesWithRetry, fetchInterviewsWithRetry, fetchJobPostings]);

  // 旧インライン版 InterviewModal は外部コンポーネントへ移行済みのため削除しました。

  // 候補者登録モーダル
  const CandidateModal = () => {
    // ローカルフォーム状態を使用して入力の独立性を保証
    const [localForm, setLocalForm] = useState({
      name: '',
      email: '',
      phone: '',
      position: '',
      jobTypeId: '',
      jobTypeLabel: '',
      candidateType: 'chuuto' as 'chuuto' | 'shinsotsu',
      // experience, skillsフィールドは現在DBスキーマに存在しないため削除
    });

    const [assigneeUserIds, setAssigneeUserIds] = useState<string[]>([]);
    const [jobTypes, setJobTypes] = useState<JobType[]>([]);
    const [jobTypeLoading, setJobTypeLoading] = useState(false);
    const [companyUsers, setCompanyUsers] = useState<
      { id: string; email: string; displayName?: string }[]
    >([]);

    // モーダルが開かれるたびにローカル状態をリセット
    useEffect(() => {
      setLocalForm({
        name: '',
        email: '',
        phone: '',
        position: '',
        jobTypeId: '',
        jobTypeLabel: '',
        candidateType: 'chuuto',
        // experience, skillsフィールドは現在DBスキーマに存在しないため削除
      });
      setAssigneeUserIds([]);
    }, []);

    useEffect(() => {
      const fetchUsers = async () => {
        try {
          const res = await companyApi.getCompanyUsers(companyId);
          if (res.success && res.data) {
            // IDを文字列化して一貫性を担保
            setCompanyUsers(res.data.map((u: any) => ({ ...u, id: String(u.id) })));
          } else {
            console.error(res.message || 'ユーザー一覧の取得に失敗しました');
          }
        } catch (e) {
          console.error('会社ユーザー一覧の取得に失敗', e);
        }
      };
      fetchUsers();
    }, []);

    // 職種マスタの取得（企業の選択済み職種優先、未設定時は全職種）
    useEffect(() => {
      const fetchJobTypes = async () => {
        try {
          setJobTypeLoading(true);
          const selectedRes = await companyApi.getCompanySelectedJobTypes(companyId);
          if (selectedRes.success && selectedRes.data?.selectedJobTypes && selectedRes.data.selectedJobTypes.length > 0) {
            setJobTypes(
              selectedRes.data.selectedJobTypes.map((s) => ({
                id: s.jobTypeId,
                name: s.jobTypeName,
              }))
            );
          } else {
            const res = await jobTypeApi.getJobTypes();
            if (res.success && Array.isArray(res.data)) {
              setJobTypes(res.data);
            } else {
              setJobTypes([]);
            }
          }
        } catch (e) {
          console.error('職種一覧の取得に失敗', e);
        } finally {
          setJobTypeLoading(false);
        }
      };
      fetchJobTypes();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // jobTypeId / jobTypeLabel を position に反映（後方互換用）
      const position =
        localForm.jobTypeLabel ||
        jobTypes.find((jt) => jt.id === localForm.jobTypeId)?.name ||
        localForm.position;

      await handleCandidateFormSubmit({
        ...localForm,
        position,
        assigneeUserIds,
      });
    };

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        {/* 画面が低いとパネルが画面外にはみ出し、下端の「登録」ボタンが押せなくなる。
            パネルを画面高に収め、入力欄だけスクロールさせて操作バーは常に見せる。 */}
        <div className="bg-white rounded-lg w-full max-w-md max-h-full flex flex-col overflow-hidden">
          <div className="flex justify-between items-center px-6 pt-6 pb-4 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">候補者登録</h3>
            <button
              onClick={() => setShowCandidateModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
            <div className="space-y-4 overflow-y-auto px-6 pb-2 flex-1 min-h-0">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">氏名</label>
              <input
                type="text"
                value={localForm.name}
                onChange={(e) => setLocalForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="山田 太郎"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">メールアドレス</label>
              <input
                type="email"
                value={localForm.email}
                onChange={(e) => setLocalForm((prev) => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="yamada@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">電話番号</label>
              <input
                type="tel"
                value={localForm.phone}
                onChange={(e) => setLocalForm((prev) => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="090-1234-5678"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                担当者（複数選択可）
              </label>
              <div className="max-h-40 overflow-auto border rounded-md divide-y">
                {companyUsers.map((u) => {
                  const userId = String(u.id);
                  const checked = assigneeUserIds.includes(userId);
                  return (
                    <label key={userId} className="flex items-center gap-2 p-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setAssigneeUserIds((prev) => {
                            if (e.target.checked) {
                              return prev.includes(userId) ? prev : [...prev, userId];
                            }
                            return prev.filter((existingId) => existingId !== userId);
                          });
                        }}
                      />
                      <span>{u.displayName || u.email}</span>
                    </label>
                  );
                })}
                {companyUsers.length === 0 && (
                  <div className="p-2 text-sm text-gray-500">ユーザーが見つかりません</div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">職種</label>
              <select
                value={localForm.jobTypeId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selected = jobTypes.find((jt) => jt.id === selectedId);
                  setLocalForm((prev) => ({
                    ...prev,
                    jobTypeId: selectedId,
                    jobTypeLabel: selected ? selected.name : prev.jobTypeLabel,
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                required
                disabled={jobTypeLoading || jobTypes.length === 0}
              >
                <option value="" disabled>
                  {jobTypeLoading ? '職種を読み込み中...' : '職種を選択してください'}
                </option>
                {jobTypes.map((jobType) => (
                  <option key={jobType.id} value={jobType.id}>
                    {jobType.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">採用区分</label>
              <div className="flex gap-2">
                {([
                  { value: 'chuuto', label: '中途' },
                  { value: 'shinsotsu', label: '新卒' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setLocalForm((prev) => ({ ...prev, candidateType: opt.value }))}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      localForm.candidateType === opt.value
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                AI面接の質問が採用区分で切り替わります（中途は職務経歴、新卒は学生時代を確認）。
              </p>
            </div>

            </div>

            {/* スクロールしても常に見える操作バー */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowCandidateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <Save size={16} />
                登録
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // 候補者編集モーダル
  const EditCandidateModal: React.FC<EditCandidateModalProps> = ({ candidate }) => {
    // ローカルフォーム状態（Hooksは常に最初に呼び出す）
    const [localForm, setLocalForm] = useState({
      name: candidate.name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      position: candidate.position || '',
      // 未設定の既存データは、DBの既定値と同じ「中途」を初期選択にする
      candidateType: candidate.candidateType || ('chuuto' as 'chuuto' | 'shinsotsu'),
      jobTypeId: '',
      jobTypeLabel: candidate.position || '',
    });
    const [assigneeUserIds, setAssigneeUserIds] = useState<string[]>(
      (candidate.assigneeUserIds || []).map((id) => String(id))
    );
    const [companyUsers, setCompanyUsers] = useState<
      { id: string; email: string; displayName?: string }[]
    >([]);
    const [jobTypes, setJobTypes] = useState<JobType[]>([]);
    const [jobTypeLoading, setJobTypeLoading] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    // candidate が変更された時にフォームを更新（職種ラベルは position から設定）
    useEffect(() => {
      setLocalForm((prev) => ({
        ...prev,
        name: candidate.name,
        email: candidate.email,
        phone: candidate.phone || '',
        position: candidate.position || '',
        jobTypeLabel: candidate.position || '',
      }));
      setAssigneeUserIds(candidate.assigneeUserIds || []);
    }, [candidate]);

    // 企業ユーザー一覧取得
    useEffect(() => {
      const fetchUsers = async () => {
        try {
          const res = await companyApi.getCompanyUsers(companyId);
          if (res.success && res.data) {
            // IDを文字列化して一貫性を担保
            setCompanyUsers(res.data.map((u: any) => ({ ...u, id: String(u.id) })));
          } else {
            console.error(res.message || 'ユーザー一覧の取得に失敗しました');
          }
        } catch (e) {
          console.error('会社ユーザー一覧の取得に失敗', e);
        }
      };
      fetchUsers();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 職種マスタの取得 + 初期選択（企業の選択済み職種優先、未設定時は全職種）
    useEffect(() => {
      const fetchJobTypes = async () => {
        try {
          setJobTypeLoading(true);
          let list: JobType[] = [];
          const selectedRes = await companyApi.getCompanySelectedJobTypes(companyId);
          if (selectedRes.success && selectedRes.data?.selectedJobTypes && selectedRes.data.selectedJobTypes.length > 0) {
            list = selectedRes.data.selectedJobTypes.map((s) => ({
              id: s.jobTypeId,
              name: s.jobTypeName,
            }));
          } else {
            const res = await jobTypeApi.getJobTypes();
            if (res.success && Array.isArray(res.data)) {
              list = res.data;
            }
          }
          setJobTypes(list);

          // candidate.position と同じ名前の職種があれば初期選択
          if (candidate.position) {
            const matched = list.find((jt) => jt.name === candidate.position);
            if (matched) {
              setLocalForm((prev) => ({
                ...prev,
                jobTypeId: matched.id,
                jobTypeLabel: matched.name,
              }));
            }
          }
        } catch (e) {
          console.error('職種一覧の取得に失敗', e);
        } finally {
          setJobTypeLoading(false);
        }
      };
      fetchJobTypes();
    }, [candidate.position]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      try {
          const position =
            localForm.jobTypeLabel ||
            jobTypes.find((jt) => jt.id === localForm.jobTypeId)?.name ||
            localForm.position;

          await handleUpdateCandidate({
            ...localForm,
            position,
            assigneeUserIds,
          });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <>
        {/* オーバーレイ */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
          onClick={() => setShowEditCandidateModal(false)}
        />

        {/* モーダル */}
        <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
          {/* 画面が低いと下端の「更新」ボタンが画面外に出て押せなくなるため、
              パネルを画面高に収めて入力欄だけスクロールさせる */}
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-full flex flex-col overflow-hidden transform transition-all duration-300">
            {/* ヘッダー */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">候補者情報編集</h3>
              <button
                onClick={() => setShowEditCandidateModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* フォーム */}
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">名前 *</label>
                <input
                  type="text"
                  value={localForm.name}
                  onChange={(e) => setLocalForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス *
                </label>
                <input
                  type="email"
                  value={localForm.email}
                  onChange={(e) => setLocalForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">電話番号</label>
                <input
                  type="tel"
                  value={localForm.phone}
                  onChange={(e) => setLocalForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* 担当者選択 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  担当者（複数選択可）
                </label>
                <div className="max-h-40 overflow-auto border rounded-md divide-y">
                  {companyUsers.map((u) => {
                    const userId = String(u.id);
                    const checked = assigneeUserIds.includes(userId);
                    return (
                      <label key={userId} className="flex items-center gap-2 p-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setAssigneeUserIds((prev) => {
                              if (e.target.checked) {
                                return prev.includes(userId) ? prev : [...prev, userId];
                              }
                              return prev.filter((existingId) => existingId !== userId);
                            });
                          }}
                        />
                        <span>{u.displayName || u.email}</span>
                      </label>
                    );
                  })}
                  {companyUsers.length === 0 && (
                    <div className="p-2 text-sm text-gray-500">ユーザーが見つかりません</div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">職種 *</label>
                <select
                  value={localForm.jobTypeId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selected = jobTypes.find((jt) => jt.id === selectedId);
                    setLocalForm((prev) => ({
                      ...prev,
                      jobTypeId: selectedId,
                      jobTypeLabel: selected ? selected.name : prev.jobTypeLabel,
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                  required
                  disabled={jobTypeLoading || jobTypes.length === 0}
                >
                  <option value="" disabled>
                    {jobTypeLoading ? '職種を読み込み中...' : '職種を選択してください'}
                  </option>
                  {jobTypes.map((jobType) => (
                    <option key={jobType.id} value={jobType.id}>
                      {jobType.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 採用区分（登録モーダルと同じUI） */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">採用区分</label>
                <div className="flex gap-2">
                  {([
                    { value: 'chuuto', label: '中途' },
                    { value: 'shinsotsu', label: '新卒' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setLocalForm((prev) => ({ ...prev, candidateType: opt.value }))
                      }
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        localForm.candidateType === opt.value
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  AI面接の質問が採用区分で切り替わります（中途は職務経歴、新卒は学生時代を確認）。
                </p>
              </div>

              </div>

              {/* ボタン（スクロールしても常に見える） */}
              <div className="flex gap-3 p-6 pt-4 border-t border-gray-200 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowEditCandidateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                >
                  {submitting ? '更新中...' : '更新'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  };

  // 削除確認ダイアログ
  const DeleteConfirmDialog = () => {
    const [deleting, setDeleting] = useState(false);

    // selectedCandidateがない場合は何もレンダリングしない
    if (!selectedCandidate) return null;

    const handleDelete = async () => {
      setDeleting(true);
      try {
        await handleDeleteCandidate();
      } finally {
        setDeleting(false);
      }
    };

    return (
      <>
        {/* オーバーレイ */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
          onClick={() => setShowDeleteConfirmDialog(false)}
        />

        {/* ダイアログ */}
        <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md transform transition-all duration-300">
            {/* ヘッダー */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">候補者削除の確認</h3>
              </div>
              <button
                onClick={() => setShowDeleteConfirmDialog(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* 内容 */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  以下の候補者を本当に削除しますか？この操作は取り消せません。
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{selectedCandidate.name}</div>
                      <div className="text-sm text-gray-500">{selectedCandidate.email}</div>
                      <div className="text-sm text-gray-500">{selectedCandidate.position}</div>
                    </div>
                  </div>
                </div>
              </div>

              {selectedCandidate.interviews.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm font-medium">注意</span>
                  </div>
                  <p className="text-sm text-yellow-700 mt-1">
                    この候補者には{selectedCandidate.interviews.length}
                    件の面接履歴があります。削除すると関連する面接情報もすべて削除されます。
                  </p>
                </div>
              )}

              {/* ボタン */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmDialog(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      削除中...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      削除する
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // 面接削除確認ダイアログ
  const InterviewDeleteConfirmDialog = () => {
    const [deleting, setDeleting] = useState(false);

    if (!interviewToDelete) return null;

    const handleDelete = async () => {
      setDeleting(true);
      try {
        await handleDeleteInterview();
      } finally {
        setDeleting(false);
      }
    };

    return (
      <>
        {/* オーバーレイ */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
          style={{ zIndex: 10001 }}
          onClick={() => setShowInterviewDeleteConfirmDialog(false)}
        />

        {/* ダイアログ */}
        <div
          className="fixed inset-0 flex items-center justify-center z-[60] p-4"
          style={{ zIndex: 10002 }}
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md transform transition-all duration-300">
            {/* ヘッダー */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">面接削除の確認</h3>
              </div>
              <button
                onClick={() => setShowInterviewDeleteConfirmDialog(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* 内容 */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  以下の面接を本当に削除しますか？この操作は取り消せません。
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-900 font-medium">
                      {formatCandidateName(interviewToDelete.candidateName)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {interviewToDelete.startDate} ～ {interviewToDelete.endDate}
                    </div>
                    <div className="text-xs text-gray-500">{interviewToDelete.type}</div>
                  </div>
                </div>
              </div>

              {/* ボタン */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowInterviewDeleteConfirmDialog(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      削除中...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      削除する
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // 面接詳細モーダル
  const InterviewDetailModal = () => {
    const [editingUrl, setEditingUrl] = useState(false);
    const [tempUrl, setTempUrl] = useState(selectedInterview?.interviewUrl || '');

    if (!selectedInterview) return null;

    const currentUrl = selectedInterview.interviewUrl || generateInterviewUrl(selectedInterview.id);

    const handleUrlSave = async () => {
      if (tempUrl.trim()) {
        await updateInterviewUrl(selectedInterview.id, tempUrl.trim());
        setEditingUrl(false);
      }
    };

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        {/* 内容が長いと下端のボタンが画面外に出るため、画面高に収めて中身をスクロールさせる */}
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-full overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              面接詳細 - {formatCandidateName(selectedInterview.candidateName)}
            </h3>
            <button
              onClick={() => {
                setShowInterviewDetailModal(false);
                setSelectedInterview(null);
                setEditingUrl(false);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-6">
            {/* 基本情報 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">基本情報</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">候補者名</label>
                  <p className="text-sm text-gray-900">
                    {formatCandidateName(selectedInterview.candidateName)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">職種</label>
                  <p className="text-sm text-gray-900">{selectedInterview.position}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">面接種別</label>
                  <p className="text-sm text-gray-900">
                    {safeToInterviewTypeLabel(selectedInterview.type)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">面接官</label>
                  <p className="text-sm text-gray-900">{selectedInterview.interviewer}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">面接可能期間</label>
                  <p className="text-sm text-gray-900">
                    {selectedInterview.startDate}
                    {selectedInterview.endDate && selectedInterview.endDate !== selectedInterview.startDate && (
                      <span className="text-gray-500"> 〜 {selectedInterview.endDate}</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">面接完了日時</label>
                  <p className="text-sm text-gray-900">
                    {selectedInterview.completedAt
                      ? new Date(selectedInterview.completedAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">応募求人</label>
                  <p className="text-sm text-gray-900">
                    {selectedInterview.jobPostingId
                      ? jobPostings.find((jp) => jp.id === selectedInterview.jobPostingId)?.title ||
                        '不明'
                      : '未設定'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">ステータス</label>
                  <span
                    className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getInterviewStatusColor(selectedInterview.status)}`}
                  >
                    {getInterviewStatusText(selectedInterview.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* 面接URL管理 */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-semibold text-gray-900">面接URL</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingUrl(!editingUrl)}
                    className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
                  >
                    <Settings size={14} />
                    {editingUrl ? 'キャンセル' : '編集'}
                  </button>
                </div>
              </div>

              {editingUrl ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">面接URL</label>
                    <input
                      type="url"
                      value={tempUrl}
                      onChange={(e) => setTempUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="https://meet.google.com/xxx-xxxx-xxx"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleUrlSave}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <Save size={16} />
                      保存
                    </button>
                    <button
                      onClick={() => {
                        setTempUrl(selectedInterview.interviewUrl || '');
                        setEditingUrl(false);
                      }}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={() => {
                        setInterviewToDelete(selectedInterview);
                        setShowInterviewDeleteConfirmDialog(true);
                      }}
                      className="ml-auto bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
                    >
                      <Trash2 size={16} />
                      削除
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={currentUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(currentUrl, selectedInterview.id)}
                      className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                        copiedUrls[selectedInterview.id]
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {copiedUrls[selectedInterview.id] ? (
                        <>
                          <Check size={16} />
                          コピー済み
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          コピー
                        </>
                      )}
                    </button>
                    {currentUrl && (
                      <button
                        onClick={() => window.open(currentUrl, '_blank')}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-2"
                      >
                        <ExternalLink size={16} />
                        開く
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    この面接URLを候補者に共有してください。面接ID:{' '}
                    {selectedInterview.sessionId || selectedInterview.id}
                  </p>
                </div>
              )}
            </div>

            {/* スコア情報（評価済みの場合） */}
            {selectedInterview.score && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3">評価</h4>
                <div className="flex items-center gap-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {selectedInterview.score}~点
                  </div>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={20}
                        className={`${i < Math.floor((selectedInterview.score || 0) / 20) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* アクションボタン */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowInterviewDetailModal(false);
                  setSelectedInterview(null);
                  setEditingUrl(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                閉じる
              </button>
              <button 
                onClick={() => {
                  setShowInterviewDetailModal(false);
                  setShowEvaluationSidebar(true);
                }}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2">
                <Edit size={16} />
                面接情報編集
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 候補者管理画面
  const CandidatesTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900">候補者管理</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              setLoading(true);
              console.log('🔄 手動データ更新開始...');

              // 楽観的更新をリセット
              setIsOptimisticMode(false);
              setOptimisticUpdates({ candidates: [], interviews: [] });

              // キャッシュを完全にクリア
              cache.invalidate();

              // 強制的にキャッシュを使わずに最新データを取得
              await Promise.all([
                fetchCandidatesWithRetry(false),
                fetchInterviewsWithRetry(false),
                fetchJobPostings(),
              ]);

              setLoading(false);
              console.log('✅ 手動データ更新完了');
            }}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm disabled:bg-gray-400"
          >
            <svg
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {loading ? '更新中...' : 'データ更新'}
          </button>
          <button
            onClick={() => {
              // 候補者登録モーダル表示のみ
              setShowCandidateModal(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 shadow-sm"
          >
            <Plus size={18} />
            新規候補者追加
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="候補者名、メール、職種で検索..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap">
          <Filter size={18} />
          フィルター
        </button>
      </div>

      <div
        ref={candidateTableRef}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden scroll-mt-4"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    候補者
                    {getSortIcon('name')}
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('position')}
                >
                  <div className="flex items-center gap-2">
                    職種
                    {getSortIcon('position')}
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('candidateType')}
                >
                  <div className="flex items-center gap-2">
                    区分
                    {getSortIcon('candidateType')}
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-2">
                    ステータス
                    {getSortIcon('status')}
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('inviteDate')}
                >
                  <div className="flex items-center gap-2">
                    招待日
                    {getSortIcon('inviteDate')}
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  次回面接
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('evaluation')}
                >
                  <div className="flex items-center gap-2">
                    評価
                    {getSortIcon('evaluation')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {candidatePagination.pageItems.map((candidate) => (
                <tr key={candidate.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className="flex items-center cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                      onClick={() => {
                        fetchCandidateWithInterviews(candidate.id);
                      }}
                    >
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-green-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                        <div className="text-sm text-gray-500">{candidate.email}</div>
                        <div className="text-sm text-gray-500">{candidate.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{candidate.position}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCandidateTypeColor(candidate.candidateType)}`}
                    >
                      {getCandidateTypeLabel(candidate.candidateType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(candidate.status)}`}
                    >
                      {getStatusText(candidate.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {candidate.inviteDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {candidate.interviews.length > 0 ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <span>{candidate.interviews[0].date}</span>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(candidate.interviews[0].status)}`}
                          >
                            {candidate.status === 'pending'
                              ? `${candidate.interviews.length}回目`
                              : ''}
                            {getStatusText(candidate.interviews[0].status)}
                          </span>
                        </div>
                        <div className="text-gray-500 text-xs mt-1">
                          {candidate.interviews[0].timeRange}
                        </div>
                        <div className="text-gray-500 text-xs">{candidate.interviews[0].type}</div>
                      </div>
                    ) : (
                      <span className="text-gray-500">未設定</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {candidate.evaluation ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {candidate.evaluation.totalScore}~点
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">未評価</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={candidatePagination.page}
          totalPages={candidatePagination.totalPages}
          totalItems={candidatePagination.totalItems}
          startIndex={candidatePagination.startIndex}
          endIndex={candidatePagination.endIndex}
          pageSize={candidatePagination.pageSize}
          onPageChange={candidatePagination.setPage}
          onPageSizeChange={candidatePagination.setPageSize}
          itemLabel="件"
        />
      </div>
    </div>
  );

  const tabs = [
    { id: 'candidates', label: '候補者管理', icon: Users },
    { id: 'interviews', label: '面接管理', icon: Calendar },
  ];

  // 面接詳細表示ハンドラ
  const handleViewInterviewDetail = useCallback((interview: Interview) => {
    const candidate = candidates.find(c => c.id === interview.candidateId || c.name === interview.candidateName);
    setSelectedCandidate(candidate);
    setSelectedInterview(interview);
    setShowInterviewDetailModal(true);
  }, [candidates]);

  // 面接削除クリックハンドラ
  const handleDeleteInterviewClick = useCallback((interview: Interview) => {
    setInterviewToDelete(interview);
    setShowInterviewDeleteConfirmDialog(true);
  }, []);

  const renderTabContent = () => {
    if (currentTab === 'candidates') {
      // <CandidatesTab /> とコンポーネントとして描画すると、親の再レンダリングごとに
      // 別コンポーネント扱いでタブ全体が再マウントされ、検索欄が1文字入力するたびに
      // フォーカスを失う（実キーボード入力・IMEで入力不能になる）。関数呼び出しで
      // インライン展開し、コンポーネント境界を作らない。
      return CandidatesTab();
    }
    return (
      <InterviewsTab
        interviews={displayInterviews}
        interviewSearchTerm={interviewSearchTerm}
        interviewStatusFilter={interviewStatusFilter}
        setInterviewStatusFilter={setInterviewStatusFilter}
        interviewSortConfig={interviewSortConfig}
        setInterviewSortConfig={setInterviewSortConfig}
        formatCandidateName={formatCandidateName}
        generateInterviewUrl={generateInterviewUrl}
        getInterviewStatusColor={getInterviewStatusColor}
        getInterviewStatusText={getInterviewStatusText}
        copyToClipboard={copyToClipboard}
        copiedUrls={copiedUrls}
        onViewDetail={handleViewInterviewDetail}
        onDeleteInterview={handleDeleteInterviewClick}
      />
    );
  };

  // ローディング状態表示（改善版）
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            {/* アニメーション付きローディングスピナー */}
            <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-t-blue-400 rounded-full animate-ping mx-auto"></div>
          </div>
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">データを読み込み中...</h3>
            <div className="flex justify-center space-x-1">
              <div
                className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                style={{ animationDelay: '0ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                style={{ animationDelay: '150ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-3">
              候補者データと面接スケジュールを取得しています
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      {/* ヘッダー */}
      <AdminHeader
        companyId={companyId}
        companyName={companyName}
        title="Talent Scan"
        subtitle="候補者と面接スケジュールを管理します"
        currentPage="dashboard"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* エラー表示（改善版） */}
        {error && (
          <div
            className={`p-4 rounded-lg border-l-4 mb-6 ${
              errorType === 'network'
                ? 'bg-orange-50 border-orange-400 text-orange-800'
                : errorType === 'auth'
                  ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
                  : errorType === 'validation'
                    ? 'bg-blue-50 border-blue-400 text-blue-800'
                    : 'bg-red-50 border-red-400 text-red-800'
            }`}
          >
            <div className="flex justify-between items-start">
              <div className="flex">
                <div className="flex-shrink-0">
                  {errorType === 'network'
                    ? '🌐'
                    : errorType === 'auth'
                      ? '🔒'
                      : errorType === 'validation'
                        ? '📝'
                        : '⚠️'}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">{error}</p>
                  {retryCount > 0 && retryCount < MAX_RETRY_COUNT && (
                    <p className="text-xs mt-1 opacity-75">
                      再試行中... ({retryCount}/{MAX_RETRY_COUNT})
                    </p>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                {(errorType === 'network' || errorType === 'server') &&
                  retryCount >= MAX_RETRY_COUNT && (
                    <button
                      onClick={() => {
                        setRetryCount(0);
                        setError(null);
                        setErrorType(null);
                        fetchCandidatesWithRetry(false);
                        fetchInterviewsWithRetry(false);
                      }}
                      className="text-xs bg-white px-2 py-1 rounded border border-current hover:bg-current hover:text-white transition-colors"
                    >
                      再試行
                    </button>
                  )}
                <button
                  onClick={() => {
                    setError(null);
                    setErrorType(null);
                    setRetryCount(0);
                  }}
                  className="text-lg leading-none hover:opacity-70"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* タブナビゲーション */}
        <div className="mb-6">
          <nav className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setCurrentTab(tab.id)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    currentTab === tab.id
                      ? 'bg-white text-green-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <Icon
                    className={`mr-2 ${currentTab === tab.id ? 'text-green-600' : 'text-gray-400'}`}
                    size={18}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* 面接管理タブのヘッダーと検索 */}
        {currentTab === 'interviews' && (
          <>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
              <h2 className="text-xl font-bold text-gray-900">面接管理</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={async () => {
                    setLoading(true);
                    console.log('🔄 手動データ更新開始...');
                    setIsOptimisticMode(false);
                    setOptimisticUpdates({ candidates: [], interviews: [] });
                    cache.invalidate();
                    await Promise.all([
                      fetchCandidatesWithRetry(false),
                      fetchInterviewsWithRetry(false),
                      fetchJobPostings(),
                    ]);
                    setLoading(false);
                    console.log('✅ 手動データ更新完了');
                  }}
                  disabled={loading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm disabled:bg-gray-400"
                >
                  <svg
                    className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {loading ? '更新中...' : 'データ更新'}
                </button>
                <button
                  onClick={() => {
                    setInterviewForm({
                      candidateId: '',
                      startDate: '',
                      endDate: '',
                      type: '一次面接',
                      interviewer: '',
                      jobPostingId: '',
                    });
                    setShowInterviewModal(true);
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 shadow-sm"
                >
                  <Plus size={18} />
                  面接予定追加
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="候補者名で検索..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  value={interviewSearchTerm}
                  onChange={(e) => setInterviewSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {renderTabContent()}
      </div>

      {/* モーダル */}
      {showInterviewModal && (
        <RecruiterInterviewModal
          key="interview-modal"
          show={showInterviewModal}
          onClose={() => {
            setShowInterviewModal(false);
            setInterviewForm({
              candidateId: '',
              startDate: '',
              endDate: '',
              type: '一次面接',
              interviewer: '',
              jobPostingId: '',
            });
          }}
          candidates={candidates}
          interviewForm={interviewForm}
          setInterviewForm={setInterviewForm}
          companyId={companyId}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          setError={setError}
          setErrorType={setErrorType}
          handleError={handleError}
          fetchInterviewsWithRetry={fetchInterviewsWithRetry}
          performOptimisticUpdate={performOptimisticUpdate}
          confirmOptimisticUpdate={confirmOptimisticUpdate}
          rollbackOptimisticUpdate={rollbackOptimisticUpdate}
          jobPostings={jobPostings}
          jobPostingLoading={jobPostingLoading}
        />
      )}
      {showCandidateModal && <CandidateModal key="candidate-modal" />}
      {showEditCandidateModal && selectedCandidate && (
        <EditCandidateModal key="edit-candidate-modal" candidate={selectedCandidate} />
      )}
      {showDeleteConfirmDialog && <DeleteConfirmDialog key="delete-confirm-dialog" />}
      {showInterviewDeleteConfirmDialog && (
        <InterviewDeleteConfirmDialog key="interview-delete-confirm-dialog" />
      )}
      <EvaluationSidebar
        showEvaluationSidebar={showEvaluationSidebar}
        setShowEvaluationSidebar={setShowEvaluationSidebar}
        selectedCandidate={selectedCandidate}
        selectedInterviewIndex={selectedInterviewIndex}
        setSelectedInterviewIndex={setSelectedInterviewIndex}
        onScheduleInterview={() => handleScheduleInterview(selectedCandidate!)}
        onEditCandidate={handleEditCandidate}
        onDeleteCandidate={() => {
          setShowDeleteConfirmDialog(true);
        }}
      />
      {showInterviewDetailModal && <InterviewDetailModal />}
    </div>
  );
};

export default CompleteInterviewManagement;
